import { Locale, PurchaseOrder, ShippingDocumentsTabType } from "@shared/types";
import { COUNTRY_CODE_TO_VENDOR_REGION, REGIONS_WITH_LOCALE } from "./constants";

export type GroupOption = "product" | "grind" | "quantity";
/**
 * Get locale from country code
 */
export const getLocaleFromCountryCode = (countryCode?: string | null): Locale => {
  const cc = (countryCode ?? "").toUpperCase();
  const vendorRegion = COUNTRY_CODE_TO_VENDOR_REGION[cc];
  return vendorRegion ? REGIONS_WITH_LOCALE[vendorRegion] : "en_US";
};

/**
 * Get the first line item's product name from a purchase order
 */
export const getFirstProductName = (order: PurchaseOrder): string => {
  return order.lineItems?.[0]?.productTitle ?? "";
};

/**
 * Get the first line item's grind type from a purchase order
 */
export const getFirstGrind = (order: PurchaseOrder): string => {
  return order.lineItems?.[0]?.grindType ?? "";
};

/**
 * Get the first line item's quantity from a purchase order
 */
export const getFirstQuantity = (order: PurchaseOrder): string => {
  return order.lineItems?.[0]?.quantity ?? "";
};

/**
 * Extract values from composite keys (format: "prefix:value | prefix:value")
 * Uses string methods instead of regex for better performance in sorting operations
 */
const extractValue = (key: string, prefix: string): string => {
  const searchPrefix = `${prefix}:`;
  const prefixIndex = key.indexOf(searchPrefix);
  if (prefixIndex === -1) {
    return "";
  }
  const valueStart = prefixIndex + searchPrefix.length;
  const separatorIndex = key.indexOf(" | ", valueStart);
  const valueEnd = separatorIndex === -1 ? key.length : separatorIndex;
  return key.slice(valueStart, valueEnd).trim();
};

/**
 * Group purchase orders by multiple criteria using composite keys
 */
export const groupPurchaseOrders = (orders: PurchaseOrder[], groupOptions: Set<GroupOption>): PurchaseOrder[] => {
  if (groupOptions.size === 0) {
    return orders;
  }

  const grouped = new Map<string, PurchaseOrder[]>();

  for (const order of orders) {
    // Create a composite key from all selected group options
    const keyParts: string[] = [];

    if (groupOptions.has("product")) {
      keyParts.push(`product:${getFirstProductName(order)}`);
    }
    if (groupOptions.has("grind")) {
      keyParts.push(`grind:${getFirstGrind(order)}`);
    }
    if (groupOptions.has("quantity")) {
      keyParts.push(`quantity:${getFirstQuantity(order)}`);
    }

    const key = keyParts.join(" | ");

    if (!grouped.has(key)) {
      grouped.set(key, []);
    }
    const group = grouped.get(key);
    if (group) {
      group.push(order);
    }
  }

  // Sort groups based on grouping options priority: product title, then quantity, then grind
  const sortedGroups = Array.from(grouped.entries()).sort(([keyA], [keyB]) => {
    // Priority order: product title, then quantity, then grind
    if (groupOptions.has("product")) {
      const productA = extractValue(keyA, "product");
      const productB = extractValue(keyB, "product");
      if (productA !== productB) {
        return productA.localeCompare(productB);
      }
    }

    if (groupOptions.has("quantity")) {
      const quantityA = extractValue(keyA, "quantity");
      const quantityB = extractValue(keyB, "quantity");
      if (quantityA !== quantityB) {
        // Parse as numbers for numeric comparison
        const numA = Number.parseFloat(quantityA) || 0;
        const numB = Number.parseFloat(quantityB) || 0;
        return numA - numB;
      }
    }

    if (groupOptions.has("grind")) {
      const grindA = extractValue(keyA, "grind");
      const grindB = extractValue(keyB, "grind");
      if (grindA !== grindB) {
        return grindA.localeCompare(grindB);
      }
    }

    // If all values are equal, maintain stable order
    return 0;
  });

  const result: PurchaseOrder[] = [];
  for (const [, group] of sortedGroups) {
    // Within each group, sort by line item count: 1 line item first, then more than 1
    // Use createdAt as secondary sort key for stable sorting
    const sortedGroup = group.sort((a, b) => {
      const aLineItemCount = a.lineItems?.length ?? 0;
      const bLineItemCount = b.lineItems?.length ?? 0;

      // Primary sort: line item count (1 line item first, then more than 1)
      if (aLineItemCount !== bLineItemCount) {
        if (aLineItemCount === 1 && bLineItemCount > 1) {
          return -1;
        }
        if (aLineItemCount > 1 && bLineItemCount === 1) {
          return 1;
        }
        return aLineItemCount - bLineItemCount;
      }

      // Secondary sort: createdAt for stable sorting
      const aCreatedAt = new Date(a.createdAt).getTime();
      const bCreatedAt = new Date(b.createdAt).getTime();
      return aCreatedAt - bCreatedAt;
    });

    result.push(...sortedGroup);
  }

  return result;
};

export const generateShippingDocumentFilename = (
  documentType: ShippingDocumentsTabType,
  vendorLocale: Locale,
  date: Date = new Date(),
  batchId?: string,
  isSingleOrder = false
): string => {
  const prefixes: Record<ShippingDocumentsTabType, string> = {
    "shipping-label": isSingleOrder ? "shipping-label" : "shipping-labels",
    "packing-slip": isSingleOrder ? "packing-slip" : "packing-slips",
    combined: "shipping-labels-and-packing-slips",
    manifest: "manifest",
  };

  const dateStr = date
    .toLocaleDateString(vendorLocale.replaceAll("_", "-"), { year: "numeric", month: "2-digit", day: "2-digit" })
    .replaceAll(/[^a-zA-Z0-9]/g, "-");
  const batch = batchId ? `-batch-${batchId.slice(-5).toUpperCase()}` : "";

  return `${prefixes[documentType] ?? "shipping-documents"}${batch}-${dateStr}.pdf`;
};

export const downloadBlobWithFilename = (blobUrl: string, filename: string): Promise<void> => {
  return fetch(blobUrl)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Failed to fetch blob: ${response.status} ${response.statusText}`);
      }
      return response.blob();
    })
    .then((blob) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    });
};

/**
 * Creates an HTML wrapper blob URL that embeds a PDF and intercepts downloads
 * to use the specified filename instead of the blob UUID.
 *
 * Limitations:
 * - Browser's native PDF viewer download button may still use UUID filename
 *   due to security restrictions (browser controls the viewer UI)
 * - Right-click context menu and keyboard shortcuts (Ctrl+S/Cmd+S) are intercepted
 * - The Download button in the dialog always uses the correct filename
 */
/**
 * Escapes HTML special characters to prevent XSS attacks
 */
const escapeHtml = (text: string): string => {
  const map: Record<string, string> = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;",
  };
  return text.replaceAll(/[&<>"']/g, (char) => map[char] ?? char);
};

export const createPdfWrapperUrl = (pdfBlobUrl: string, filename: string): string => {
  const escapedFilename = JSON.stringify(filename);
  const escapedPdfUrl = JSON.stringify(pdfBlobUrl);
  const htmlEscapedFilename = escapeHtml(filename);

  const html = String.raw`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${htmlEscapedFilename}</title>
  <style>
    html, body {
      margin: 0;
      padding: 0;
      width: 100%;
      height: 100%;
      overflow: hidden;
    }
    embed {
      width: 100%;
      height: 100vh;
      border: none;
    }
  </style>
</head>
<body>
  <embed src=${escapedPdfUrl} type="application/pdf" />
  <script>
    (function() {
      const pdfUrl = ${escapedPdfUrl};
      const filename = ${escapedFilename};
      
      function downloadPdf() {
        fetch(pdfUrl)
          .then(response => response.blob())
          .then(blob => {
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
          })
          .catch(error => console.error('Error downloading PDF:', error));
      }
      
      // Intercept right-click context menu
      document.addEventListener('contextmenu', function(e) {
        e.preventDefault();
        downloadPdf();
      }, true);
      
      // Intercept keyboard shortcuts (Ctrl+S, Cmd+S)
      document.addEventListener('keydown', function(e) {
        if ((e.ctrlKey || e.metaKey) && e.key === 's') {
          e.preventDefault();
          downloadPdf();
        }
      }, true);
    })();
  </script>
</body>
</html>`;

  const blob = new Blob([html], { type: "text/html" });
  return URL.createObjectURL(blob);
};
